import React, { useRef, useMemo } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import ShotCardGrid from './ShotCardGrid';

const VirtualGrid = ({ rows, gridViewProps, columnCount = 4 }) => {
  const parentRef = useRef(null);

  // 确保 rows 是数组
  const safeRows = Array.isArray(rows) ? rows : [];

  // 计算行数
  const rowCount = useMemo(() => 
    Math.ceil(safeRows.length / columnCount),
    [safeRows.length, columnCount]
  );

  // 虚拟滚动配置
  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 250,
    overscan: 2,
    gap: 16,
  });

  const virtualItems = virtualizer.getVirtualItems();

  // 如果数据量小，使用普通渲染
  if (safeRows.length < 50) {
    return (
      <div className="absolute inset-0 overflow-y-auto custom-scrollbar px-6 py-4 pb-32">
        <div 
          className="grid gap-4"
          style={{ 
            gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` 
          }}
        >
          {safeRows.map((scene, index) => (
            <ShotCardGrid
              key={scene.id || index}
              index={index}
              scene={scene}
              {...gridViewProps}
            />
          ))}
        </div>
      </div>
    );
  }

  // 渲染虚拟行
  const renderVirtualRow = (virtualRow) => {
    const rowIndex = virtualRow.index;
    const startIndex = rowIndex * columnCount;
    const endIndex = Math.min(startIndex + columnCount, safeRows.length);
    const rowItems = safeRows.slice(startIndex, endIndex);

    return (
      <div
        key={virtualRow.key}
        data-index={virtualRow.index}
        ref={virtualizer.measureElement}
        className="grid gap-4 mb-4"
        style={{
          gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
        }}
      >
        {rowItems.map((scene, colIndex) => {
          const actualIndex = startIndex + colIndex;
          return (
            <ShotCardGrid
              key={scene.id || actualIndex}
              index={actualIndex}
              scene={scene}
              {...gridViewProps}
            />
          );
        })}
      </div>
    );
  };

  return (
    <div
      ref={parentRef}
      className="absolute inset-0 overflow-y-auto custom-scrollbar px-6 py-4 pb-32"
    >
      <div
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: '100%',
          position: 'relative',
        }}
      >
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            transform: `translateY(${virtualItems[0]?.start ?? 0}px)`,
          }}
        >
          {virtualItems.map(renderVirtualRow)}
        </div>
      </div>
    </div>
  );
};

export default VirtualGrid;
